
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { pb } from '@/lib/pocketbase';

interface LoginFormProps {
  redirectTo?: string;
  inviteToken?: string;
}

const LoginForm = ({ redirectTo = '/dashboard', inviteToken }: LoginFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const { login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const getErrorMessage = (error: any) => {
    console.log('Login error details:', error);
    
    // Handle PocketBase specific errors
    if (error?.status) {
      switch (error.status) {
        case 400:
          if (error.message?.includes('invalid_credentials') || 
              error.message?.includes('Failed to authenticate') ||
              error.data?.message?.includes('invalid_credentials')) {
            return "Invalid email or password. Please check your credentials and try again.";
          }
          if (error.message?.includes('validation')) {
            return "Please enter a valid email address and password.";
          }
          return "Invalid request. Please check your input and try again.";
        
        case 401:
          return "Invalid email or password. Please check your credentials and try again.";
        
        case 403:
          return "Your account may be suspended or not activated. Please contact support.";
        
        case 404:
          return "Account not found. Please check your email or create a new account.";
        
        case 429:
          return "Too many login attempts. Please wait a few minutes before trying again.";
        
        case 500:
          return "Server error. Please try again later or contact support.";
        
        default:
          return "Login failed. Please try again.";
      }
    }
    
    // Handle generic error messages
    if (error?.message) {
      const message = error.message.toLowerCase();
      
      if (message.includes('invalid_credentials') || 
          message.includes('invalid credentials') ||
          message.includes('failed to authenticate')) {
        return "Invalid email or password. Please check your credentials and try again.";
      }
      
      if (message.includes('network') || message.includes('fetch')) {
        return "Network error. Please check your internet connection and try again.";
      }
      
      if (message.includes('timeout')) {
        return "Request timeout. Please try again.";
      }
      
      if (message.includes('validation')) {
        return "Please enter a valid email address and password.";
      }
      
      if (message.includes('suspended') || message.includes('banned')) {
        return "Your account has been suspended. Please contact support.";
      }
      
      if (message.includes('verified') || message.includes('confirm')) {
        return "Please verify your email address before logging in.";
      }
    }
    
    // Default error message
    return "An unexpected error occurred. Please try again or contact support if the problem persists.";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Missing Information",
        description: "Please enter both email and password.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation - optimized regex
    if (!email.includes('@') || !email.includes('.')) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      await login(email, password);
      
      // Claim group booking invite if token exists
      if (inviteToken) {
        try {
          const userId = pb.authStore.model?.id;
          console.log('🎟️ Claiming invite on login for user:', userId, 'Token:', inviteToken);
          
          const invites = await pb.collection('group_booking_invites').getFullList({
            filter: `invite_token = "${inviteToken}"`
          });
          
          if (invites.length > 0) {
            const invite = invites[0];
            console.log('🎟️ Found invite:', invite.id);
            
            if (!invite.claimed_by_user_id || invite.claimed_by_user_id === '') {
              await pb.collection('group_booking_invites').update(invite.id, {
                is_claimed: true,
                claimed_by_user_id: userId
              });
              toast({
                title: "Welcome back!",
                description: "Your group booking has been linked to your account.",
              });
            } else {
              toast({
                title: "Welcome back!",
                description: "You have successfully signed in.",
              });
            }
          }
        } catch (inviteError) {
          console.error('Error claiming invite:', inviteError);
          toast({
            title: "Welcome back!",
            description: "You have successfully signed in.",
          });
        }
      } else {
        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });
      }
      
      // Wait a moment for auth state to update, then navigate
      setTimeout(() => {
        // Get the fresh user data from authStore after login
        const currentUser = pb.authStore.model;
        let destination = redirectTo;
        
        if (currentUser?.role === 'admin') {
          destination = '/admin';
        }
        
        // Redirect to intended destination
        navigate(destination, { replace: true });
      }, 100);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      
      toast({
        title: "Login Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      toast({
        title: "Missing Email",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotPasswordEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsForgotPasswordLoading(true);
    
    try {
      console.log('Sending password reset for:', forgotPasswordEmail);
      const result = await pb.collection('users').requestPasswordReset(forgotPasswordEmail);
      console.log('Password reset result:', result);
      
      // Force success since PocketBase returned successfully
      toast({
        title: "Password Reset Email Sent",
        description: "Check your email (including spam folder) for password reset instructions.",
      });
      
      setShowForgotPasswordDialog(false);
      setForgotPasswordEmail('');
    } catch (error: any) {
      console.error('Password reset error:', error);
      console.error('Error details:', {
        status: error?.status,
        message: error?.message,
        data: error?.data,
        toString: error?.toString()
      });
      
      // Log the exact error that's causing the red flash
      console.log('FLASH ERROR MESSAGE:', error?.message || error?.toString() || 'Unknown error');
      
      let errorMessage = "Failed to send password reset email. Please try again.";
      
      if (error?.status === 404) {
        errorMessage = "No account found with this email address.";
      } else if (error?.status === 429) {
        errorMessage = "Too many requests. Please wait before trying again.";
      } else if (error?.status === 400) {
        errorMessage = "Invalid email address or email sending is not configured.";
      }
      
      toast({
        title: "Password Reset Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
            autoComplete="email"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className="pr-10"
              autoComplete="current-password"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </Button>
          </div>
        </div>
        
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing In...
            </>
          ) : (
            'Sign In'
          )}
        </Button>
        
        <div className="text-center">
          <Dialog open={showForgotPasswordDialog} onOpenChange={setShowForgotPasswordDialog}>
            <DialogTrigger asChild>
              <Button variant="link" className="text-sm">
                Forgot your password?
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </form>

      {/* Forgot password dialog moved outside the main form to prevent submit conflicts */}
      <Dialog open={showForgotPasswordDialog} onOpenChange={setShowForgotPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="Enter your email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
                disabled={isForgotPasswordLoading}
                autoComplete="email"
              />
            </div>
            <Button type="submit" className="w-full" disabled={isForgotPasswordLoading}>
              {isForgotPasswordLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoginForm;
